import { z } from 'zod';

/**
 * Alerts contract (CLAUDE.md §10 `/api/alerts`, §11.1 `/alerts`, §15). Single source of truth
 * for the alert-log + acknowledgement payloads — imported by the Worker `alerts` routes, their
 * tests, and the `/alerts` screen. Alerts originate from edge events (persisted to D1 by the
 * ingestion consumer) and from cloud scoring; the cockpit surfaces open ones, the `/alerts`
 * screen the full log. Reading is `alerts:read`; acknowledging is `alerts:ack`. Every ack writes
 * an immutable audit row (§12.3).
 */

export const AlertSeveritySchema = z.enum(['info', 'warning', 'critical']);
export type AlertSeverity = z.infer<typeof AlertSeveritySchema>;

export const AlertStateSchema = z.enum(['open', 'acknowledged', 'cleared']);
export type AlertState = z.infer<typeof AlertStateSchema>;

/** Severity rank for sorting/threshold logic — higher is more urgent. */
export const ALERT_SEVERITY_RANK: Record<AlertSeverity, number> = {
  info: 0,
  warning: 1,
  critical: 2,
};

// ── Read row ──────────────────────────────────────────────────────────────────
export const AlertRowSchema = z.object({
  id: z.string(),
  siteId: z.string(),
  severity: AlertSeveritySchema,
  /** Source tag code (e.g. `tag_sd_incomer_kw`), or null for site-level alerts. */
  sourceTag: z.string().nullable(),
  kind: z.string(),
  message: z.string(),
  state: AlertStateSchema,
  /** Raised timestamp, epoch ms. */
  raisedAt: z.number(),
  ackBy: z.string().nullable(),
  /** Acknowledgement timestamp, epoch ms, or null while open. */
  ackAt: z.number().nullable(),
});
export type AlertRow = z.infer<typeof AlertRowSchema>;

export const AlertListResponseSchema = z.object({ alerts: z.array(AlertRowSchema) });
export type AlertListResponse = z.infer<typeof AlertListResponseSchema>;

// ── Acknowledge ───────────────────────────────────────────────────────────────
/** Ack input — optional note recorded in the audit trail. */
export const AlertAckInputSchema = z.object({
  note: z.string().max(2000).optional(),
});
export type AlertAckInput = z.infer<typeof AlertAckInputSchema>;

export const AlertAckResponseSchema = z.object({ alert: AlertRowSchema });
export type AlertAckResponse = z.infer<typeof AlertAckResponseSchema>;
